'use client';

import { useEffect, useRef, useState } from 'react';
import { convertVideoToGif } from '@/lib/browser-gif-tools';
import { readVideoMetadata } from '@/lib/browser-video-tools';
import { clamp, formatFileSize, getBaseName } from '@/lib/browser-image-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import { acceptedVideoMimeTypes, formatDurationMs } from '@/components/tools/gif-tool-shared';

type QualityPreset = 'high' | 'balanced' | 'smaller';
type LoopMode = 'infinite' | 'once' | 'custom';

type LoadedVideoSource = {
  file: File;
  previewUrl: string;
  width: number;
  height: number;
  durationMs: number;
};

// The GIF is made by ffmpeg, so a format the browser cannot play (AVI, MKV, TS, WMV) is fine;
// only the metadata read needs the ffmpeg fallback that readVideoMetadata provides.
async function loadVideoMetadata(file: File): Promise<LoadedVideoSource> {
  const metadata = await readVideoMetadata(file);
  return {
    file,
    previewUrl: URL.createObjectURL(file),
    width: metadata.width,
    height: metadata.height,
    durationMs: metadata.durationMs,
  };
}

export default function BrowserVideoToGif({
  heading,
  toolSlug,
}: { heading?: string; toolSlug?: string } = {}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [source, setSource] = useState<LoadedVideoSource | null>(null);
  const [outputUrl, setOutputUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [startMs, setStartMs] = useState(0);
  const [endMs, setEndMs] = useState(3000);
  const [targetWidth, setTargetWidth] = useState(480);
  const [fps, setFps] = useState(12);
  const [qualityPreset, setQualityPreset] = useState<QualityPreset>('balanced');
  const [loopMode, setLoopMode] = useState<LoopMode>('infinite');
  const [customLoopCount, setCustomLoopCount] = useState(3);

  useEffect(() => {
    return () => {
      if (source?.previewUrl) {
        URL.revokeObjectURL(source.previewUrl);
      }

      if (outputUrl) {
        URL.revokeObjectURL(outputUrl);
      }
    };
  }, [outputUrl, source]);

  function resetOutputState() {
    if (outputUrl) {
      URL.revokeObjectURL(outputUrl);
    }

    setOutputUrl(null);
    setDownloadName(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      setSource((current) => {
        if (current?.previewUrl) {
          URL.revokeObjectURL(current.previewUrl);
        }

        return null;
      });
      return;
    }

    try {
      const nextSource = await loadVideoMetadata(file);

      setSource((current) => {
        if (current?.previewUrl) {
          URL.revokeObjectURL(current.previewUrl);
        }

        return nextSource;
      });
      setStartMs(0);
      setEndMs(Math.min(nextSource.durationMs, 5000));
      setTargetWidth(Math.min(640, nextSource.width || 640));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'The video could not be loaded.');
    }
  }

  async function runConversion() {
    if (!source) {
      setErrorMessage('Choose a video first.');
      return;
    }

    if (endMs <= startMs) {
      setErrorMessage('Set an end time that is later than the start time.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        toolSlug ?? 'video-to-gif',
        'Credits are not available for another video to GIF conversion right now.',
      );
      const outputBlob = await convertVideoToGif({
        file: source.file,
        startMs,
        endMs,
        width: targetWidth,
        fps,
        loopCount:
          loopMode === 'infinite' ? 0 : loopMode === 'once' ? 1 : clamp(customLoopCount, 1, 999),
        qualityPreset,
      });
      const nextOutputUrl = URL.createObjectURL(outputBlob);

      resetOutputState();
      setOutputUrl(nextOutputUrl);
      setDownloadName(`${getBaseName(source.file.name)}.gif`);
      setResultSize(outputBlob.size);
      setCreditMessage(formatToolCreditSuccess('Video to GIF conversion', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The video could not be converted to GIF.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  const clipLengthMs = Math.max(0, endMs - startMs);

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{heading ?? 'Convert a video clip to GIF'}</h2>
          <p>
            Upload one video, choose a short clip range, set width and frame rate, and export a GIF
            locally in the browser.
          </p>
        </div>
        <span className="tool-live-badge">GIF</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.file.name : 'Choose one video or drop it here'}</strong>
              <span>
                {source
                  ? `${formatFileSize(source.file.size)} • ${source.width} × ${source.height}`
                  : 'Short clips work best for local browser conversion'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept={acceptedVideoMimeTypes}
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Start (ms)</span>
              <input
                min="0"
                onChange={(event) => {
                  setStartMs(Math.max(0, Number(event.target.value) || 0));
                  resetOutputState();
                }}
                type="number"
                value={startMs}
              />
            </label>
            <label className="tool-live-field">
              <span>End (ms)</span>
              <input
                min="0"
                onChange={(event) => {
                  setEndMs(Math.max(0, Number(event.target.value) || 0));
                  resetOutputState();
                }}
                type="number"
                value={endMs}
              />
            </label>
          </div>

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Width</span>
              <input
                min="120"
                onChange={(event) => {
                  setTargetWidth(Math.max(120, Number(event.target.value) || 120));
                  resetOutputState();
                }}
                type="number"
                value={targetWidth}
              />
            </label>
            <label className="tool-live-field">
              <span>FPS</span>
              <select
                onChange={(event) => {
                  setFps(Number(event.target.value));
                  resetOutputState();
                }}
                value={fps}
              >
                {[8, 10, 12, 15, 20].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Quality</span>
              <select
                onChange={(event) => {
                  setQualityPreset(event.target.value as QualityPreset);
                  resetOutputState();
                }}
                value={qualityPreset}
              >
                <option value="high">High</option>
                <option value="balanced">Balanced</option>
                <option value="smaller">Smaller file</option>
              </select>
            </label>
            <label className="tool-live-field">
              <span>Loop mode</span>
              <select
                onChange={(event) => {
                  setLoopMode(event.target.value as LoopMode);
                  resetOutputState();
                }}
                value={loopMode}
              >
                <option value="infinite">Infinite</option>
                <option value="once">Play once</option>
                <option value="custom">Custom count</option>
              </select>
            </label>
          </div>

          {loopMode === 'custom' ? (
            <div className="tool-live-controls">
              <label className="tool-live-field">
                <span>Custom loops</span>
                <input
                  min="1"
                  onChange={(event) => {
                    setCustomLoopCount(clamp(Number(event.target.value) || 1, 1, 999));
                    resetOutputState();
                  }}
                  type="number"
                  value={customLoopCount}
                />
              </label>
            </div>
          ) : null}

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing}
              onClick={() => void runConversion()}
              type="button"
            >
              {isProcessing ? 'Converting…' : 'Convert to GIF'}
            </button>
            {outputUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={outputUrl}>
                Download GIF
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Clip details</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Video duration</strong>
              <span>{source ? formatDurationMs(source.durationMs) : 'Add a video'}</span>
            </div>
            <div>
              <strong>Clip length</strong>
              <span>{formatDurationMs(clipLengthMs)}</span>
            </div>
            <div>
              <strong>Warning</strong>
              <span>{clipLengthMs > 10000 ? 'Long clip' : 'Short clip recommended'}</span>
            </div>
            {resultSize ? (
              <div>
                <strong>Result size</strong>
                <span>{formatFileSize(resultSize)}</span>
              </div>
            ) : null}
          </div>
        </aside>
      </div>

      {source ? (
        <div className="tool-live-preview tool-compression-preview">
          <h3>Preview</h3>
          <div className="tool-compression-preview-grid">
            <div className="tool-live-preview-frame">
              <video controls muted playsInline preload="metadata" src={source.previewUrl} />
            </div>
            <div className="tool-live-preview-frame">
              {outputUrl ? (
                <img alt="Converted GIF preview" src={outputUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>GIF preview</strong>
                  <span>Run the conversion to preview the generated GIF.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </article>
  );
}
